import React, { type CSSProperties } from "react";
import {
  ChevronLeft,
  ChevronRight,
  ExternalLink,
  MessageCircle,
  Quote,
  ShieldCheck,
  Star,
} from "lucide-react";
import {
  customerReviewsTokens,
  type CustomerReviewsAvatarTone,
  type CustomerReviewsColorToken,
  type CustomerReviewsIconSlot,
  type CustomerReviewsIconToken,
} from "./CustomerReviews.tokens";

export type CustomerReviewsLinkComponent = React.ComponentType<{
  href: string;
  children?: React.ReactNode;
  className?: string;
  title?: string;
  target?: string;
  rel?: string;
  "aria-label"?: string;
  [key: string]: any;
}>;

export type CustomerReviewsAvatar = {
  src?: string;
  alt?: string;
  initials?: string;
  tone?: CustomerReviewsAvatarTone;
  backgroundColor?: string;
  textColor?: string;
  width?: number;
  height?: number;
};

export type CustomerReview = {
  id?: string | number;
  name: string;
  text: string;
  dateText?: string;
  rating?: number;
  avatar?: CustomerReviewsAvatar;
};

export type CustomerReviewsColors = Partial<Record<CustomerReviewsColorToken, string>>;

export type CustomerReviewsIconTokens = Partial<
  Record<CustomerReviewsIconSlot, CustomerReviewsIconToken>
>;

export type CustomerReviewsProps = {
  Link?: CustomerReviewsLinkComponent;
  reviews?: CustomerReview[];
  badgeText?: React.ReactNode;
  title?: React.ReactNode;
  titleComponent?: "h1" | "h2" | "h3";
  rating?: string;
  ratingLabel?: string;
  reviewsHref?: string;
  reviewsLinkLabel?: React.ReactNode;
  reviewsLinkAriaLabel?: string;
  reviewsLinkTitle?: string;
  reviewsLinkTarget?: string;
  reviewsLinkRel?: string;
  showReviewsLinkIcon?: boolean;
  footerText?: React.ReactNode;
  dotCount?: number;
  activeDotIndex?: number;
  initialReviewIndex?: number;
  showControls?: boolean;
  showDots?: boolean;
  showFooter?: boolean;
  previousLabel?: string;
  nextLabel?: string;
  icons?: CustomerReviewsIconTokens;
  colors?: CustomerReviewsColors;
  colours?: CustomerReviewsColors;
  accentColor?: string;
  backgroundColor?: string;
  backgroundColour?: string;
  surfaceColor?: string;
  textColor?: string;
  maxWidth?: number | string;
  id?: string;
  className?: string;
  style?: CSSProperties;
  ariaLabel?: string;
};

type CssVarStyle = CSSProperties &
  Record<`--customer-reviews-${string}`, string | number | undefined>;

type AvatarCssVarStyle = CSSProperties &
  Record<`--customer-reviews-avatar-${string}`, string | undefined>;

const t = customerReviewsTokens;

const iconRegistry: Record<CustomerReviewsIconToken, React.ElementType> = {
  message: MessageCircle,
  star: Star,
  quote: Quote,
  shield: ShieldCheck,
  chevronLeft: ChevronLeft,
  chevronRight: ChevronRight,
  externalLink: ExternalLink,
};

const customerReviewsCss = `
.customer-reviews {
  --customer-reviews-bg: ${t.colors.background};
  --customer-reviews-surface: ${t.colors.surface};
  --customer-reviews-text: ${t.colors.text};
  --customer-reviews-text-soft: ${t.colors.textSoft};
  --customer-reviews-text-muted: ${t.colors.textMuted};
  --customer-reviews-accent: ${t.colors.accent};
  --customer-reviews-accent-dark: ${t.colors.accentDark};
  --customer-reviews-accent-soft: ${t.colors.accentSoft};
  --customer-reviews-accent-softer: ${t.colors.accentSofter};
  --customer-reviews-border: ${t.colors.border};
  --customer-reviews-divider: ${t.colors.divider};
  --customer-reviews-dot: ${t.colors.dot};
  --customer-reviews-arrow: ${t.colors.arrow};
  --customer-reviews-max-width: ${t.sizes.maxWidth}px;
  width: 100%;
  overflow: hidden;
  background: var(--customer-reviews-bg);
  color: var(--customer-reviews-text);
  font-family: ${t.typography.fontFamily};
  box-sizing: border-box;
  container-type: inline-size;
}
.customer-reviews,
.customer-reviews * {
  box-sizing: border-box;
}
.customer-reviews-shell {
  width: 100%;
  max-width: var(--customer-reviews-max-width);
  margin: 0 auto;
  padding: ${t.spacing.shellY}px ${t.spacing.shellX}px;
}
.customer-reviews-header {
  display: grid;
  justify-items: center;
  text-align: center;
}
.customer-reviews-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  max-width: 100%;
  gap: 9px;
  padding: 10px 18px;
  border-radius: ${t.radius.pill}px;
  background: var(--customer-reviews-accent-soft);
  color: var(--customer-reviews-accent);
  font-size: ${t.typography.badge.fontSize}px;
  line-height: ${t.typography.badge.lineHeight};
  font-weight: ${t.typography.badge.fontWeight};
  letter-spacing: ${t.typography.badge.letterSpacing};
}
.customer-reviews-badge-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
.customer-reviews-badge svg {
  flex: 0 0 auto;
}
.customer-reviews-title {
  max-width: 980px;
  margin: 26px 0 0;
  color: var(--customer-reviews-text);
  font-size: ${t.typography.title.fontSize}px;
  line-height: ${t.typography.title.lineHeight};
  font-weight: ${t.typography.title.fontWeight};
  letter-spacing: ${t.typography.title.letterSpacing};
}
.customer-reviews-summary {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  justify-content: center;
  gap: 12px 18px;
  margin-top: 28px;
  color: var(--customer-reviews-text-muted);
  font-size: ${t.typography.summary.fontSize}px;
  line-height: ${t.typography.summary.lineHeight};
  font-weight: ${t.typography.summary.fontWeight};
  letter-spacing: ${t.typography.summary.letterSpacing};
}
.customer-reviews-summary-stars {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--customer-reviews-accent);
}
.customer-reviews-summary-stars svg {
  width: 25px;
  height: 25px;
}
.customer-reviews-score {
  color: var(--customer-reviews-text);
  font-size: ${t.typography.score.fontSize}px;
  line-height: ${t.typography.score.lineHeight};
  font-weight: ${t.typography.score.fontWeight};
  letter-spacing: ${t.typography.score.letterSpacing};
}
.customer-reviews-summary-divider {
  display: inline-block;
  width: 1px;
  height: 28px;
  background: var(--customer-reviews-divider);
}
.customer-reviews-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-width: 0;
  color: var(--customer-reviews-text-muted);
  text-decoration: none;
  transition: color 160ms ease;
}
.customer-reviews-link:hover {
  color: var(--customer-reviews-accent);
}
.customer-reviews-link:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--customer-reviews-accent) 24%, transparent);
  outline-offset: 4px;
  border-radius: 6px;
}
.customer-reviews-link-label {
  min-width: 0;
  overflow-wrap: anywhere;
}
.customer-reviews-carousel {
  position: relative;
  width: 100%;
  margin-top: 60px;
  padding: 0 74px;
}
.customer-reviews-viewport {
  width: 100%;
  overflow-x: auto;
  overflow-y: visible;
  scroll-behavior: smooth;
  scroll-snap-type: x mandatory;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
  padding: 4px 2px 30px;
}
.customer-reviews-viewport::-webkit-scrollbar {
  display: none;
}
.customer-reviews-viewport:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--customer-reviews-accent) 22%, transparent);
  outline-offset: 7px;
  border-radius: ${t.radius.card}px;
}
.customer-reviews-track {
  display: flex;
  align-items: stretch;
  gap: ${t.spacing.cardGap}px;
}
.customer-reviews-card {
  display: flex;
  flex: 0 0 calc((100% - (${t.spacing.cardGap}px * 2)) / 3);
  min-width: 0;
  min-height: 340px;
  scroll-margin-inline: 12px;
  scroll-snap-align: start;
}
.customer-reviews-card-inner {
  display: flex;
  flex-direction: column;
  width: 100%;
  min-width: 0;
  padding: 38px 30px 28px;
  border: 1px solid var(--customer-reviews-border);
  border-radius: ${t.radius.card}px;
  background: var(--customer-reviews-surface);
  box-shadow: ${t.colors.shadow};
}
.customer-reviews-card-header {
  display: flex;
  align-items: center;
  gap: 18px;
  min-width: 0;
}
.customer-reviews-avatar {
  display: inline-flex;
  flex: 0 0 ${t.sizes.avatar}px;
  width: ${t.sizes.avatar}px;
  height: ${t.sizes.avatar}px;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  border-radius: ${t.radius.control}px;
  background: var(--customer-reviews-avatar-bg, ${t.avatarTones.rose.background});
  color: var(--customer-reviews-avatar-color, ${t.avatarTones.rose.text});
  font-size: 27px;
  line-height: 1;
  font-weight: 850;
  letter-spacing: 0;
}
.customer-reviews-avatar-image {
  display: block;
  width: 100%;
  height: 100%;
  object-fit: cover;
}
.customer-reviews-card-meta {
  display: grid;
  min-width: 0;
  gap: 10px;
}
.customer-reviews-name {
  margin: 0;
  color: var(--customer-reviews-text);
  font-size: ${t.typography.name.fontSize}px;
  line-height: ${t.typography.name.lineHeight};
  font-weight: ${t.typography.name.fontWeight};
  letter-spacing: ${t.typography.name.letterSpacing};
  overflow-wrap: anywhere;
}
.customer-reviews-card-rating-row {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 7px;
  min-width: 0;
}
.customer-reviews-card-stars {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  color: var(--customer-reviews-accent);
}
.customer-reviews-card-stars svg {
  width: 16px;
  height: 16px;
}
.customer-reviews-star-empty {
  color: var(--customer-reviews-dot);
}
.customer-reviews-card-date {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: var(--customer-reviews-text-muted);
  font-size: ${t.typography.meta.fontSize}px;
  line-height: ${t.typography.meta.lineHeight};
  font-weight: ${t.typography.meta.fontWeight};
  letter-spacing: ${t.typography.meta.letterSpacing};
  white-space: nowrap;
}
.customer-reviews-card-date::before {
  content: "";
  display: inline-block;
  width: 3px;
  height: 3px;
  border-radius: ${t.radius.control}px;
  background: currentColor;
  opacity: 0.8;
}
.customer-reviews-body {
  margin: 38px 0 0;
  color: var(--customer-reviews-text);
  font-size: ${t.typography.body.fontSize}px;
  line-height: ${t.typography.body.lineHeight};
  font-weight: ${t.typography.body.fontWeight};
  letter-spacing: ${t.typography.body.letterSpacing};
}
.customer-reviews-quote {
  display: inline-flex;
  width: 42px;
  height: 42px;
  align-items: center;
  justify-content: center;
  margin-top: auto;
  border-radius: ${t.radius.control}px;
  background: var(--customer-reviews-accent-soft);
  color: var(--customer-reviews-accent);
}
.customer-reviews-quote svg {
  width: 20px;
  height: 20px;
}
.customer-reviews-control {
  position: absolute;
  top: 50%;
  z-index: 2;
  display: inline-flex;
  width: ${t.sizes.control}px;
  height: ${t.sizes.control}px;
  align-items: center;
  justify-content: center;
  border-radius: ${t.radius.control}px;
  background: var(--customer-reviews-surface);
  color: var(--customer-reviews-arrow);
  box-shadow: ${t.colors.arrowShadow};
  text-decoration: none;
  transform: translateY(-50%);
  transition: color 160ms ease, transform 160ms ease, box-shadow 160ms ease;
}
.customer-reviews-control:hover {
  color: var(--customer-reviews-accent);
  transform: translateY(-50%) scale(1.03);
}
.customer-reviews-control:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--customer-reviews-accent) 24%, transparent);
  outline-offset: 4px;
}
.customer-reviews-control-previous {
  left: 0;
}
.customer-reviews-control-next {
  right: 0;
}
.customer-reviews-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 13px;
  margin-top: 10px;
}
.customer-reviews-dot {
  display: inline-flex;
  width: 10px;
  height: 10px;
  border-radius: ${t.radius.control}px;
  background: var(--customer-reviews-dot);
  text-decoration: none;
  transition: background 160ms ease, transform 160ms ease;
}
.customer-reviews-dot:hover {
  transform: scale(1.15);
  background: color-mix(in srgb, var(--customer-reviews-accent) 45%, var(--customer-reviews-dot));
}
.customer-reviews-dot:focus-visible {
  outline: 3px solid color-mix(in srgb, var(--customer-reviews-accent) 24%, transparent);
  outline-offset: 4px;
}
.customer-reviews-dot-active {
  background: var(--customer-reviews-accent);
}
.customer-reviews-footer {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 14px;
  max-width: 760px;
  margin: 50px auto 0;
  color: var(--customer-reviews-text);
  font-size: ${t.typography.trust.fontSize}px;
  line-height: ${t.typography.trust.lineHeight};
  font-weight: ${t.typography.trust.fontWeight};
  letter-spacing: ${t.typography.trust.letterSpacing};
  text-align: left;
}
.customer-reviews-footer-icon {
  display: inline-flex;
  flex: 0 0 auto;
  color: var(--customer-reviews-accent);
}
.customer-reviews-footer-text {
  min-width: 0;
  overflow-wrap: anywhere;
}
@container (max-width: 1024px) {
  .customer-reviews-shell {
    padding: ${t.spacing.tabletShellY}px ${t.spacing.tabletShellX}px;
  }
  .customer-reviews-title {
    margin-top: 22px;
    font-size: ${t.typography.title.tabletFontSize}px;
    line-height: 1.08;
  }
  .customer-reviews-summary {
    margin-top: 22px;
    gap: 10px 14px;
    font-size: ${t.typography.summary.tabletFontSize}px;
  }
  .customer-reviews-summary-stars {
    gap: 5px;
  }
  .customer-reviews-summary-stars svg {
    width: 21px;
    height: 21px;
  }
  .customer-reviews-score {
    font-size: ${t.typography.score.tabletFontSize}px;
  }
  .customer-reviews-carousel {
    margin-top: 42px;
    padding: 0 22px;
  }
  .customer-reviews-track {
    gap: ${t.spacing.tabletCardGap}px;
  }
  .customer-reviews-card {
    flex-basis: calc((100% - (${t.spacing.tabletCardGap}px * 2)) / 3);
    min-height: 292px;
  }
  .customer-reviews-card-inner {
    padding: 24px 14px 22px;
  }
  .customer-reviews-card-header {
    gap: 10px;
  }
  .customer-reviews-avatar {
    flex-basis: ${t.sizes.tabletAvatar}px;
    width: ${t.sizes.tabletAvatar}px;
    height: ${t.sizes.tabletAvatar}px;
    font-size: 20px;
  }
  .customer-reviews-card-meta {
    gap: 8px;
  }
  .customer-reviews-name {
    font-size: ${t.typography.name.tabletFontSize}px;
  }
  .customer-reviews-card-stars svg {
    width: 12px;
    height: 12px;
  }
  .customer-reviews-card-date {
    gap: 6px;
    font-size: ${t.typography.meta.tabletFontSize}px;
  }
  .customer-reviews-body {
    margin-top: 28px;
    font-size: ${t.typography.body.tabletFontSize}px;
    line-height: 1.72;
  }
  .customer-reviews-quote {
    width: 34px;
    height: 34px;
  }
  .customer-reviews-control {
    width: 44px;
    height: 44px;
  }
  .customer-reviews-footer {
    margin-top: 36px;
  }
}
@container (max-width: 640px) {
  .customer-reviews-shell {
    padding: ${t.spacing.mobileShellY}px ${t.spacing.mobileShellX}px 32px;
  }
  .customer-reviews-badge {
    gap: 8px;
    padding: 9px 15px;
    font-size: ${t.typography.badge.mobileFontSize}px;
  }
  .customer-reviews-title {
    max-width: 310px;
    margin-top: 22px;
    font-size: ${t.typography.title.mobileFontSize}px;
    line-height: ${t.typography.title.mobileLineHeight};
  }
  .customer-reviews-summary {
    margin-top: 20px;
    gap: 9px 11px;
    font-size: ${t.typography.summary.mobileFontSize}px;
  }
  .customer-reviews-summary-stars {
    gap: 3px;
  }
  .customer-reviews-summary-stars svg {
    width: 17px;
    height: 17px;
  }
  .customer-reviews-score {
    font-size: ${t.typography.score.mobileFontSize}px;
  }
  .customer-reviews-summary-divider {
    height: 20px;
  }
  .customer-reviews-carousel {
    margin-top: 28px;
    padding: 0 30px;
  }
  .customer-reviews-viewport {
    padding-bottom: 26px;
  }
  .customer-reviews-track {
    gap: ${t.spacing.mobileCardGap}px;
  }
  .customer-reviews-card {
    flex-basis: 100%;
    min-height: 298px;
    scroll-margin-inline: 0;
  }
  .customer-reviews-card-inner {
    padding: 24px 20px 22px;
  }
  .customer-reviews-card-header {
    gap: 14px;
  }
  .customer-reviews-avatar {
    flex-basis: ${t.sizes.mobileAvatar}px;
    width: ${t.sizes.mobileAvatar}px;
    height: ${t.sizes.mobileAvatar}px;
    font-size: 22px;
  }
  .customer-reviews-name {
    font-size: ${t.typography.name.mobileFontSize}px;
  }
  .customer-reviews-card-stars svg {
    width: 13px;
    height: 13px;
  }
  .customer-reviews-card-date {
    font-size: ${t.typography.meta.mobileFontSize}px;
  }
  .customer-reviews-body {
    margin-top: 28px;
    font-size: ${t.typography.body.mobileFontSize}px;
    line-height: 1.7;
  }
  .customer-reviews-control {
    width: ${t.sizes.mobileControl}px;
    height: ${t.sizes.mobileControl}px;
  }
  .customer-reviews-control svg {
    width: 20px;
    height: 20px;
  }
  .customer-reviews-dots {
    gap: 12px;
    margin-top: 2px;
  }
  .customer-reviews-dot {
    width: 9px;
    height: 9px;
  }
  .customer-reviews-footer {
    align-items: flex-start;
    max-width: 284px;
    margin-top: 34px;
    font-size: ${t.typography.trust.mobileFontSize}px;
  }
}
@container (max-width: 360px) {
  .customer-reviews-shell {
    padding-left: 14px;
    padding-right: 14px;
  }
  .customer-reviews-carousel {
    padding: 0 26px;
  }
  .customer-reviews-card-inner {
    padding-left: 18px;
    padding-right: 18px;
  }
  .customer-reviews-control {
    width: 38px;
    height: 38px;
  }
}
`;

function getCssSize(value: number | string): string {
  return typeof value === "number" ? `${value}px` : value;
}

function sanitizeId(value: string): string {
  return value
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9_-]+/g, "-")
    .replace(/^-+|-+$/g, "") || "customer-reviews";
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function getReviewId(baseId: string, review: CustomerReview, index: number): string {
  const reviewId = review.id ? sanitizeId(String(review.id)) : `review-${index + 1}`;
  return `${baseId}-${reviewId}`;
}

function getInitials(name: string, initials?: string): string {
  if (initials) return initials.slice(0, 2).toUpperCase();

  const parts = name
    .split(/\s+/)
    .map((part) => part.trim())
    .filter(Boolean);

  if (!parts.length) return "?";

  return parts
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();
}

function getAvatarTone(tone?: CustomerReviewsAvatarTone) {
  if (tone && tone in t.avatarTones) return t.avatarTones[tone];

  return t.avatarTones.rose;
}

function TokenIcon({
  token,
  size,
  strokeWidth = 2.2,
  className,
  fill = "none",
}: {
  token: CustomerReviewsIconToken;
  size: number;
  strokeWidth?: number;
  className?: string;
  fill?: string;
}) {
  const Icon = iconRegistry[token] || Star;

  return (
    <Icon
      aria-hidden="true"
      focusable="false"
      className={className}
      size={size}
      strokeWidth={strokeWidth}
      fill={fill}
    />
  );
}

function Stars({
  rating = 5,
  label,
  icon,
  className,
}: {
  rating?: number;
  label?: string;
  icon: CustomerReviewsIconToken;
  className: string;
}) {
  const filledStars = clamp(Math.round(rating), 0, 5);

  return (
    <span className={className} aria-label={label || `${filledStars} out of 5 stars`}>
      {Array.from({ length: 5 }).map((_, index) => {
        const isFilled = index < filledStars;

        return (
          <TokenIcon
            key={index}
            token={icon}
            size={18}
            strokeWidth={1.8}
            fill={isFilled ? "currentColor" : "none"}
            className={isFilled ? undefined : "customer-reviews-star-empty"}
          />
        );
      })}
    </span>
  );
}

function ReviewCard({
  review,
  reviewId,
  icons,
}: {
  review: CustomerReview;
  reviewId: string;
  icons: Required<CustomerReviewsIconTokens>;
}) {
  const tone = getAvatarTone(review.avatar?.tone);
  const avatarStyle: AvatarCssVarStyle = {
    "--customer-reviews-avatar-bg":
      review.avatar?.backgroundColor || tone.background,
    "--customer-reviews-avatar-color": review.avatar?.textColor || tone.text,
  };

  return (
    <article id={reviewId} className="customer-reviews-card">
      <div className="customer-reviews-card-inner">
        <div className="customer-reviews-card-header">
          <div className="customer-reviews-avatar" style={avatarStyle}>
            {review.avatar?.src ? (
              <img
                className="customer-reviews-avatar-image"
                src={review.avatar.src}
                alt={review.avatar.alt || ""}
                width={review.avatar.width}
                height={review.avatar.height}
                loading="lazy"
                decoding="async"
              />
            ) : (
              <span aria-hidden="true">{getInitials(review.name, review.avatar?.initials)}</span>
            )}
          </div>

          <div className="customer-reviews-card-meta">
            <h3 className="customer-reviews-name">{review.name}</h3>
            <div className="customer-reviews-card-rating-row">
              <Stars
                rating={review.rating ?? 5}
                icon={icons.star}
                className="customer-reviews-card-stars"
                label={`${review.name} rating`}
              />
              {review.dateText ? (
                <span className="customer-reviews-card-date">{review.dateText}</span>
              ) : null}
            </div>
          </div>
        </div>

        <p className="customer-reviews-body">{review.text}</p>

        <span className="customer-reviews-quote" aria-hidden="true">
          <TokenIcon token={icons.quote} size={20} strokeWidth={2.3} />
        </span>
      </div>
    </article>
  );
}

function cloneDefaultReviews(): CustomerReview[] {
  return t.defaults.reviews.map((review) => ({
    ...review,
    avatar: review.avatar ? { ...review.avatar } : undefined,
  }));
}

export default function CustomerReviews({
  Link,
  reviews = cloneDefaultReviews(),
  badgeText = t.defaults.badgeText,
  title = t.defaults.title,
  titleComponent = "h2",
  rating = t.defaults.rating,
  ratingLabel = t.defaults.ratingLabel,
  reviewsHref = t.defaults.reviewsHref,
  reviewsLinkLabel = t.defaults.reviewsLinkLabel,
  reviewsLinkAriaLabel,
  reviewsLinkTitle,
  reviewsLinkTarget,
  reviewsLinkRel,
  showReviewsLinkIcon = false,
  footerText = t.defaults.footerText,
  dotCount,
  activeDotIndex = t.defaults.activeDotIndex,
  initialReviewIndex = t.defaults.initialReviewIndex,
  showControls = true,
  showDots = true,
  showFooter = true,
  previousLabel = "Previous review",
  nextLabel = "Next review",
  icons,
  colors,
  colours,
  accentColor,
  backgroundColor,
  backgroundColour,
  surfaceColor,
  textColor,
  maxWidth = t.sizes.maxWidth,
  id = "customer-reviews",
  className = "",
  style,
  ariaLabel,
}: CustomerReviewsProps) {
  const resolvedReviews = reviews.filter((review) => review?.name && review?.text);

  if (!resolvedReviews.length) return null;

  const safeId = sanitizeId(id);
  const headingId = `${safeId}-title`;
  const viewportId = `${safeId}-viewport`;
  const HeadingTag = titleComponent;
  const LinkComponent = (Link || "a") as React.ElementType;
  const resolvedIcons: Required<CustomerReviewsIconTokens> = {
    ...t.icons,
    ...icons,
  };

  const mergedColors = {
    ...t.colors,
    ...colours,
    ...colors,
  };

  const sectionStyle: CssVarStyle = {
    "--customer-reviews-bg":
      backgroundColor || backgroundColour || mergedColors.background,
    "--customer-reviews-surface": surfaceColor || mergedColors.surface,
    "--customer-reviews-text": textColor || mergedColors.text,
    "--customer-reviews-text-soft": mergedColors.textSoft,
    "--customer-reviews-text-muted": mergedColors.textMuted,
    "--customer-reviews-accent": accentColor || mergedColors.accent,
    "--customer-reviews-accent-dark": mergedColors.accentDark,
    "--customer-reviews-accent-soft": mergedColors.accentSoft,
    "--customer-reviews-accent-softer": mergedColors.accentSofter,
    "--customer-reviews-border": mergedColors.border,
    "--customer-reviews-divider": mergedColors.divider,
    "--customer-reviews-dot": mergedColors.dot,
    "--customer-reviews-arrow": mergedColors.arrow,
    "--customer-reviews-max-width": getCssSize(maxWidth),
    ...style,
  };

  const clampedInitialIndex = clamp(initialReviewIndex, 0, resolvedReviews.length - 1);
  const previousReviewIndex = clamp(clampedInitialIndex - 1, 0, resolvedReviews.length - 1);
  const nextReviewIndex = clamp(clampedInitialIndex + 1, 0, resolvedReviews.length - 1);
  const resolvedDotCount = Math.max(
    1,
    dotCount ?? Math.max(t.defaults.dotCount, resolvedReviews.length)
  );
  const clampedActiveDotIndex = clamp(activeDotIndex, 0, resolvedDotCount - 1);
  const showReviewControls = showControls && resolvedReviews.length > 1;
  const rel =
    reviewsLinkRel ||
    (reviewsLinkTarget === "_blank" ? "noopener noreferrer" : undefined);
  const accessibleLabel =
    ariaLabel || (typeof title === "string" ? title : "Customer reviews");

  return (
    <section
      className={`customer-reviews ${className}`.trim()}
      data-component="customer-reviews"
      aria-label={accessibleLabel}
      style={sectionStyle}
    >
      <style>{customerReviewsCss}</style>
      <div className="customer-reviews-shell">
        <header className="customer-reviews-header">
          {badgeText ? (
            <div className="customer-reviews-badge">
              <TokenIcon token={resolvedIcons.badge} size={18} strokeWidth={2.3} />
              <span className="customer-reviews-badge-text">{badgeText}</span>
            </div>
          ) : null}

          {title ? (
            <HeadingTag className="customer-reviews-title" id={headingId}>
              {title}
            </HeadingTag>
          ) : null}

          <div className="customer-reviews-summary">
            <Stars
              icon={resolvedIcons.star}
              className="customer-reviews-summary-stars"
              label={ratingLabel}
            />
            {rating ? <strong className="customer-reviews-score">{rating}</strong> : null}
            {reviewsLinkLabel ? (
              <>
                <span className="customer-reviews-summary-divider" aria-hidden="true" />
                {reviewsHref ? (
                  <LinkComponent
                    href={reviewsHref}
                    className="customer-reviews-link"
                    aria-label={reviewsLinkAriaLabel || "View all reviews"}
                    title={reviewsLinkTitle}
                    target={reviewsLinkTarget}
                    rel={rel}
                  >
                    <span className="customer-reviews-link-label">{reviewsLinkLabel}</span>
                    {showReviewsLinkIcon ? (
                      <TokenIcon
                        token={resolvedIcons.reviewsLink}
                        size={17}
                        strokeWidth={2.2}
                      />
                    ) : null}
                  </LinkComponent>
                ) : (
                  <span className="customer-reviews-link-label">{reviewsLinkLabel}</span>
                )}
              </>
            ) : null}
          </div>
        </header>

        <div className="customer-reviews-carousel">
          {showReviewControls ? (
            <a
              className="customer-reviews-control customer-reviews-control-previous"
              href={`#${getReviewId(safeId, resolvedReviews[previousReviewIndex], previousReviewIndex)}`}
              aria-label={previousLabel}
              aria-controls={viewportId}
            >
              <TokenIcon token={resolvedIcons.previous} size={26} strokeWidth={3} />
            </a>
          ) : null}

          <div
            className="customer-reviews-viewport"
            id={viewportId}
            tabIndex={0}
            aria-label="Customer review cards"
          >
            <div className="customer-reviews-track">
              {resolvedReviews.map((review, index) => (
                <ReviewCard
                  key={review.id || `${review.name}-${index}`}
                  review={review}
                  reviewId={getReviewId(safeId, review, index)}
                  icons={resolvedIcons}
                />
              ))}
            </div>
          </div>

          {showReviewControls ? (
            <a
              className="customer-reviews-control customer-reviews-control-next"
              href={`#${getReviewId(safeId, resolvedReviews[nextReviewIndex], nextReviewIndex)}`}
              aria-label={nextLabel}
              aria-controls={viewportId}
            >
              <TokenIcon token={resolvedIcons.next} size={26} strokeWidth={3} />
            </a>
          ) : null}
        </div>

        {showDots ? (
          <nav className="customer-reviews-dots" aria-label="Customer review slides">
            {Array.from({ length: resolvedDotCount }).map((_, index) => {
              const targetIndex = clamp(index, 0, resolvedReviews.length - 1);
              const isActive = index === clampedActiveDotIndex;

              return (
                <a
                  key={index}
                  className={`customer-reviews-dot ${isActive ? "customer-reviews-dot-active" : ""}`.trim()}
                  href={`#${getReviewId(safeId, resolvedReviews[targetIndex], targetIndex)}`}
                  aria-label={`Show review ${targetIndex + 1}`}
                  aria-current={isActive ? "true" : undefined}
                />
              );
            })}
          </nav>
        ) : null}

        {showFooter && footerText ? (
          <div className="customer-reviews-footer">
            <span className="customer-reviews-footer-icon" aria-hidden="true">
              <TokenIcon token={resolvedIcons.trust} size={25} strokeWidth={2.1} />
            </span>
            <span className="customer-reviews-footer-text">{footerText}</span>
          </div>
        ) : null}
      </div>
    </section>
  );
}

export { customerReviewsTokens };
